// ============================================================================
// HideBlanksContext — drop-in version of Section E.
//
// The original Section E spec called for a per-card View↔Edit toggle with
// Save/Cancel + sensitive-field Reveal + InlineEditField displayMode prop.
// This is the lightweight drop-in: a single context flag that tells child
// InlineEditField rows to hide themselves when their value is null/empty
// AND they're not in edit mode AND they're not protected/proposal-pending.
//
// Each Info-tab card provides its own context (so toggling one card doesn't
// affect another), with a small header button that flips the flag.
//
// Why this and not the full Section E refactor:
//   - 1 file change to InlineEditField (a few lines)
//   - 1 new small file (this one)
//   - 4 cards add a useState + a toggle button + a provider wrapper
//   - Solves the "every field always visible" papercut without touching
//     Save/Cancel UX, sensitive-field Reveal, or InlineEditField shape.
//
// Created 2026-05-19 evening as Section E drop-in.
// ============================================================================

import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

interface HideBlanksContextValue {
  hideBlanks: boolean;
}

const HideBlanksContext = createContext<HideBlanksContextValue>({ hideBlanks: false });

export function useHideBlanks(): boolean {
  return useContext(HideBlanksContext).hideBlanks;
}

interface ProviderProps {
  hideBlanks: boolean;
  children: ReactNode;
}

export function HideBlanksProvider({ hideBlanks, children }: ProviderProps) {
  return (
    <HideBlanksContext.Provider value={{ hideBlanks }}>
      {children}
    </HideBlanksContext.Provider>
  );
}

/**
 * Convenience helper for cards that don't want to manage their own useState —
 * returns a [hideBlanks, ToggleButton] pair. Default is `true` (hide blanks).
 *
 * Usage in a card header:
 *   const { hideBlanks, ToggleButton } = useHideBlanksControl(blankCount);
 *   ...
 *   <h3>Security &amp; access {ToggleButton}</h3>
 *   <HideBlanksProvider hideBlanks={hideBlanks}>
 *     <dl> ... </dl>
 *   </HideBlanksProvider>
 */
export function useHideBlanksControl(blankCount: number) {
  const [hideBlanks, setHideBlanks] = useState(true);
  const ToggleButton = blankCount > 0 ? (
    <button
      type="button"
      onClick={() => setHideBlanks((v) => !v)}
      className="ml-auto text-[11px] font-normal text-tcs-text-secondary underline-offset-2 hover:underline"
    >
      {hideBlanks ? `Show ${blankCount} empty` : `Hide ${blankCount} empty`}
    </button>
  ) : null;
  return { hideBlanks, ToggleButton };
}
